import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  DocumentCheckIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  ScaleIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  BuildingLibraryIcon,
  ArrowsPointingInIcon,
  EnvelopeIcon,
  BoltIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

/* ─── Animation Helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.7, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */
const capabilities = [
  {
    icon: ScaleIcon,
    title: 'Fair Lending Analysis',
    description:
      'Full IFLEP-aligned dashboard with 21 risk factors across underwriting, pricing, steering, redlining, and marketing. VBAA peer comparison for redlining risk, denial rate and pricing disparity analysis with statistical significance testing. Every analysis maps back to the specific bank partner and geographic footprint.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'HMDA LAR Validation & Filing',
    description:
      'Upload, validate against 170+ CFPB edit checks (S/V/Q/M), and prepare LAR data for submission. AI-powered explanations, field mapping for LOS imports, and geocoding validation. Critical when managing HMDA reporting across multiple bank partnerships.',
  },
  {
    icon: MagnifyingGlassIcon,
    title: 'Marketing Compliance',
    description:
      'Every ad, email, landing page, and social post checked against UDAAP, Reg Z, TILA, ECOA, Fair Housing Act, CAN-SPAM, and state-specific rules. AI-powered detection with deterministic rules engine. Examiner-ready audit trail on every review — across all 28 states.',
  },
  {
    icon: ChartBarIcon,
    title: 'Regression Analysis',
    description:
      'Logistic regression for denial models, OLS for pricing disparity. Matched-pair analysis for file review preparation. Regulator-aware output formatted for FDIC, OCC, Fed, and NCUA standards. Critical for demonstrating fair lending compliance in non-prime lending.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Regulatory Intelligence',
    description:
      'Automated monitoring of enforcement actions and regulatory changes from CFPB, OCC, FDIC, and state regulators. Know when new guidance or enforcement patterns affect non-prime lending, bank partnerships, or state-specific requirements across your 28-state footprint.',
  },
  {
    icon: DocumentTextIcon,
    title: 'Compliance Program Management',
    description:
      'Policy management with version control, compliance calendar with automated deadlines, complaint tracking, and regulatory knowledge base. Board-ready reports that pull from every module — giving bank partners visibility into compliance posture without manual assembly.',
  },
];

const bankPartnerBenefits = [
  {
    icon: ShieldCheckIcon,
    title: 'Bank Partner Confidence',
    description:
      'Bank partners need assurance that the fintech facilitating their originations maintains rigorous compliance standards. Clarid provides the documented evidence — continuous monitoring, statistical testing, and examiner-ready outputs — that gives lending partners confidence in the compliance program.',
  },
  {
    icon: BuildingLibraryIcon,
    title: 'Multi-State Regulatory Coverage',
    description:
      'Operating across 28 states means navigating a patchwork of state-specific regulatory requirements alongside federal rules. Clarid tracks state regulatory changes, monitors enforcement patterns by jurisdiction, and flags when new state-level guidance affects your operations.',
  },
  {
    icon: BoltIcon,
    title: 'Scaling Without Scaling the Compliance Team',
    description:
      '4X growth in originations means 4X the compliance workload — HMDA reporting volume, fair lending analysis complexity, marketing review cycles, and regulatory exposure. Clarid automates the execution layer so the compliance team scales with the business, not headcount.',
  },
  {
    icon: ScaleIcon,
    title: 'Non-Prime Fair Lending Documentation',
    description:
      'Lending to underbanked and non-prime borrowers increases regulatory scrutiny around fair lending, pricing equity, and UDAAP. Clarid produces the statistical analyses, regression models, and documented audit trails that demonstrate equitable treatment — proactively, not reactively.',
  },
];

const interconnectivityFlows = [
  {
    title: 'HMDA Data Flows Into Fair Lending',
    description: 'LAR entries are enriched with 50+ derived columns and fed directly into denial rate, pricing, and geographic disparity analyses — no re-upload, no re-entry.',
  },
  {
    title: 'Fair Lending Findings Feed Exam Packages',
    description: 'Statistical findings, risk levels, and affected demographic groups are automatically pulled into examiner-ready exports with full methodology documentation.',
  },
  {
    title: 'Marketing Compliance Connects to Fair Lending',
    description: 'Marketing content findings surface in fair lending risk assessments. A pattern of non-compliant ads targeting specific demographics becomes a fair lending signal, not just a marketing issue.',
  },
  {
    title: 'Regression Validates Fair Lending Disparities',
    description: 'Regression findings link to fair lending analyses, providing statistical validation for disparities identified in denial rate and pricing reviews.',
  },
  {
    title: 'Board Reports Aggregate Every Module',
    description: 'Executive-level reports pull marketing compliance stats, fair lending findings, regression results, and regulatory updates into a single cross-module view — ready for bank partner oversight committees.',
  },
];

const engagementModels = [
  {
    num: '01',
    title: 'Compliance Technology Platform',
    description:
      "Braviant deploys Clarid as the compliance operating system — continuous monitoring across marketing, HMDA, fair lending, and regulatory intelligence. Miriam and her team get a single platform that handles the analytical workload and produces examiner-ready documentation, freeing the team to focus on judgment and strategy.",
  },
  {
    num: '02',
    title: 'Bank Partner Compliance Package',
    description:
      "Clarid generates the compliance documentation bank partners need to see — fair lending statistical analyses, HMDA data quality reports, marketing compliance audit trails, and board-ready summaries. Instead of assembling these manually for each bank relationship, they're produced automatically from the same underlying data.",
  },
  {
    num: '03',
    title: 'Growth-Ready Infrastructure',
    description:
      "As Braviant expands into new states and adds bank partnerships, the compliance infrastructure scales with it. New state regulations auto-monitored, new assessment areas auto-analyzed, new marketing channels auto-scanned. The compliance program doesn't become a bottleneck to growth.",
  },
];

const dueDiligenceDocs = [
  'AI Governance Policy',
  'FS AI RMF Gap Analysis (230 control objectives mapped, 100% coverage)',
  'Model Inventory with validation framework',
  'Bias Testing Methodology & Results',
  'Data Flow Architecture Documentation',
  'Information Security Program (WISP)',
  'Incident Response Plan',
  'Business Continuity Plan',
  'Vendor Inventory (third-party dependencies)',
];

const nextSteps = [
  { num: 1, title: 'This Conversation', description: "Explore how Clarid's platform aligns with Braviant's compliance program and bank partnership requirements" },
  { num: 2, title: 'Platform Walkthrough', description: 'Live demo of fair lending analytics, marketing compliance monitoring, HMDA validation, and the compliance health dashboard' },
  { num: 3, title: 'Pilot Deployment', description: 'Structured pilot with defined success criteria — volume of originations analyzed, time savings measured, bank partner documentation generated' },
  { num: 4, title: 'Full Deployment', description: 'Production deployment across all bank partnerships with ongoing support and regulatory update monitoring' },
];

/* ─── Component ─── */
export default function BraviantPartnerPage() {
  useEffect(() => {
    document.title = 'Braviant Holdings + Clarid — AI-Native Compliance for Fintech Lending';

    const metaTags: Record<string, string> = {
      'og:title': 'Braviant Holdings + Clarid — Compliance Technology for Fintech Lending',
      'og:description': 'AI-native compliance platform for fintech lenders and their bank partners. Fair lending, HMDA, marketing compliance, and regulatory intelligence.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Braviant Holdings + Clarid',
      'twitter:description': 'Compliance technology that scales with fintech growth.',
    };

    const existingTags: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      existingTags.push(meta);
    }

    return () => {
      existingTags.forEach((tag) => document.head.removeChild(tag));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* ─── Minimal Header ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-5">
            <img src="/braviant-logo-dark.svg" alt="Braviant Holdings" className="h-8" />
            <span className="text-slate-300 text-xl font-light">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
          </div>
        </div>
      </header>

      {/* ─── Section 1: Hero ─── */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-sky-600 mb-8">
              Compliance Technology for Fintech Lending
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">
              Compliance That Scales With 4X Growth
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl lg:text-[1.65rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Braviant is expanding credit access to 50+ million underbanked Americans across 28 states
              through bank partnerships — with originations growing 4X. Clarid is the AI-native compliance
              platform that ensures the compliance program scales with that growth, not against it.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 2: The Fintech Compliance Challenge ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                The Fintech Compliance Challenge
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Fintech lenders operating through bank partnerships face a unique compliance reality:
                  every bank partner has their own regulatory expectations, every state has its own rules,
                  and every examiner wants documented evidence that the compliance program matches the
                  scale of the lending operation.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  When origination volume grows 4X, HMDA reporting volume grows 4X. Fair lending analysis
                  complexity grows exponentially — more geographies, more demographic cross-sections, more
                  statistical tests. Marketing compliance review cycles multiply across 28 states. And bank
                  partners want to see it all documented, continuously, not just at exam time.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Non-prime lending adds another dimension: heightened regulatory scrutiny around pricing
                  equity, UDAAP protections, and fair access to credit. The compliance program needs
                  to demonstrate that underbanked borrowers are treated equitably — not just assert it.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  Clarid automates the compliance execution layer — so the team focuses on strategy and
                  judgment, not spreadsheets and manual assembly.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 3: Platform Capabilities ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
              What Clarid Does
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {capabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.1}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-sky-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center group-hover:bg-sky-100 group-hover:scale-110 transition-all duration-300">
                      <cap.icon className="h-6 w-6 text-sky-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{cap.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          <FadeUp delay={0.4}>
            <p className="mt-14 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid integrates into the tools teams already use — Microsoft Copilot, Claude, ChatGPT — through
              MCP (Model Context Protocol). Compliance intelligence embedded in daily workflows.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 4: How Braviant Benefits ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 mb-8">
                How Braviant Benefits
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 max-w-5xl mx-auto leading-relaxed">
                Clarid addresses the specific compliance challenges of a high-growth fintech
                lender operating through bank partnerships across multiple states.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {bankPartnerBenefits.map((benefit, i) => (
              <ScaleIn key={benefit.title} delay={i * 0.08}>
                <div className="group p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:border-sky-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center group-hover:bg-sky-100 group-hover:scale-110 transition-all duration-300">
                      <benefit.icon className="h-6 w-6 text-sky-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{benefit.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{benefit.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 5: Cross-Module Interconnectivity ─── */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />

        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-sky-300 mb-5">
                The Differentiator
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white mb-8">
                Cross-Module Interconnectivity
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-400 max-w-5xl mx-auto leading-relaxed">
                Most compliance tools are siloed — HMDA validation in one system, fair lending in another,
                marketing compliance somewhere else. Clarid connects every module. One data upload triggers
                analysis across the entire compliance program. For a multi-state fintech lender, that
                means a single view of compliance posture across every bank partnership, every state,
                every regulatory domain.
              </p>
            </div>
          </FadeUp>

          <div className="space-y-5 lg:space-y-6 mt-14">
            {interconnectivityFlows.map((flow, i) => (
              <ScaleIn key={flow.title} delay={i * 0.08}>
                <div className="p-8 lg:p-10 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500">
                  <div className="flex items-center gap-4 mb-3">
                    <ArrowsPointingInIcon className="h-6 w-6 text-sky-400 flex-shrink-0" />
                    <h3 className="text-xl font-display font-bold text-white">{flow.title}</h3>
                  </div>
                  <p className="text-lg text-slate-400 leading-relaxed pl-10">{flow.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 6: Examination Perspective ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
              Built With the Examination Perspective
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            <ScaleIn delay={0.1}>
              <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Partners from the FDIC and Federal Reserve
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  A former FDIC Supervisory Examiner and a former Federal Reserve Supervisory
                  Examiner and fair lending subject matter expert — with decades of combined
                  examination experience — are partners in Clarid. Every rule engine, every
                  workflow, every output is validated against what examiners actually evaluate.
                  When bank partners review your compliance program, the outputs speak their language.
                </p>
              </div>
            </ScaleIn>

            <ScaleIn delay={0.2}>
              <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Built by the Buyer
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  Clarid's founder is a community bank CMO who manages marketing compliance, HMDA
                  reporting, and examiner relationships as part of his daily work. This platform
                  wasn't designed by engineers guessing at compliance needs — it was built by someone
                  who sits across from examiners using the outputs it generates. That perspective
                  shapes everything about how the platform works.
                </p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* ─── Section 7: Engagement Models ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                How This Works
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
                Clarid can serve Braviant at multiple levels — as a compliance technology platform
                for Miriam's team, as a documentation engine for bank partner relationships, and as
                growth-ready infrastructure that doesn't require scaling the compliance team linearly.
              </p>
            </FadeUp>

            <div className="space-y-6 lg:space-y-8">
              {engagementModels.map((model, i) => (
                <ScaleIn key={model.num} delay={i * 0.1}>
                  <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500">
                    <div className="flex items-start gap-6">
                      <span className="text-3xl font-display font-bold text-navy-900/20 flex-shrink-0 mt-0.5">
                        {model.num}
                      </span>
                      <div>
                        <h3 className="text-2xl font-display font-bold text-navy-900 mb-3">{model.title}</h3>
                        <p className="text-lg text-slate-500 leading-relaxed">{model.description}</p>
                      </div>
                    </div>
                  </div>
                </ScaleIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 8: Due Diligence ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                We've Done the Homework
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-14 leading-relaxed">
                Clarid maintains a comprehensive vendor due diligence package designed to meet
                interagency guidance on third-party relationships. Key documentation includes:
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="max-w-3xl mx-auto">
                <ul className="space-y-5">
                  {dueDiligenceDocs.map((doc, i) => (
                    <FadeIn key={doc} delay={0.15 + i * 0.05}>
                      <li className="flex items-start gap-4">
                        <CheckCircleIcon className="h-6 w-6 text-sky-500 flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-slate-600">{doc}</span>
                      </li>
                    </FadeIn>
                  ))}
                </ul>
              </div>
            </FadeUp>

            <FadeUp delay={0.6}>
              <p className="mt-12 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
                Full documentation available for review. SOC 2 Type I is on our 12-month roadmap —
                controls are being documented now.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 9: Moving Forward ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
                Moving Forward
              </h2>
            </FadeUp>

            <div className="space-y-10 lg:space-y-12">
              {nextSteps.map((step, i) => (
                <FadeUp key={step.num} delay={i * 0.12}>
                  <div className="flex items-start gap-7">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-navy-900 text-white flex items-center justify-center text-lg font-bold">
                      {step.num}
                    </div>
                    <div className="pt-2">
                      <h3 className="text-xl md:text-2xl font-display font-bold text-navy-900 mb-2">{step.title}</h3>
                      <p className="text-lg text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 10: Contact ─── */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder & CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-sky-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-sky-700 transition-colors">
                  bobby@clarid.ai
                </a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Minimal Footer ─── */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/braviant-logo-dark.svg" alt="Braviant Holdings" className="h-6 opacity-40" />
            <span className="text-slate-200">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
          </div>
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Clarid. Confidential.
          </p>
        </div>
      </footer>
    </div>
  );
}
